import { FontIcon, getTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import {
  IPDefaultButton,
  IPIconButton,
  IPModal,
  IPPivot,
  IPPivotItem,
  IPPrimaryButton,
  IPSeparator,
  IPText,
} from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import ActivityTrail from 'components/Activity/ActivityTrail';
import AuditTrail from 'components/Audit/AuditTrail';
import DialogConfirmDelete from 'components/Dialogs/DialogConfirmDelete';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import TaskLinkList from 'components/Links/LinkLists/TaskLinkList';
import OverlayLoader from 'components/Loading/OverlayLoader';
import { ErrorMessage } from 'components/Notification/ErrorMessage';
import TaskTypeForm from 'components/Tasks/Forms/TaskTypeForm';
import { ISingleTaskTypeProps } from 'components/Tasks/SingleTask';
import TaskTopTasksBar from 'components/Tasks/TaskTopTasksBar';
import TaskWorkflows from 'components/Tasks/TaskWorkflows';
import { getTaskViewModalStackStyles, getTaskViewModalStyles } from 'components/Tasks/View/TaskRenderHelpers';
import { cancelIcon, globalStackStylesHeight100, globalStackTokensGapSmall, templateIcon } from 'globalStyles';
import Joi from 'joi';
import Task, { TaskTypes } from 'models/tasks/task';
import TaskType, { SystemTaskTypes } from 'models/tasks/taskType';
import Webhook from 'models/webhook';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TaskDetailOwner } from '../Details/TaskDetailOwner';
import { ApprovalTemplateTaskDetail } from './ApprovalTemplateTaskDetail';

interface IApprovalTemplateTaskProps extends ISingleTaskTypeProps {
  onSaveAsNewTask: () => void;
  onChangeCompletionDate: (completionDate: Date) => void;
}

const ApprovalTemplateTask = (props: IApprovalTemplateTaskProps) => {
  const { t } = useTranslation(['task', 'control', 'translation', 'dateTimeComponent']);
  const appContext = useContext(AppContext);
  const [workflows, setWorkflows] = useState<Webhook[]>([]);
  const [err, setErr] = useState<Joi.ValidationError | undefined>(undefined);
  const [selectedPivot, setSelectedPivot] = useState<string>(
    props.selectedPivot ?? (props.task.isSeries() ? 'Workflows' : 'Context'),
  );
  const [showRemoveDialog, setShowRemoveDialog] = useState<boolean>(false);
  const [showCancelDialog, setShowCancelDialog] = useState<boolean>(false);
  const theme = getTheme();

  useEffect(() => {
    const _localizedFields = {
      assignment: t('task:Assignment.Label'),
      startDateTime: t('task:Start.Label'),
      endDateTime: t('task:End.ErrorMessage'),
      name: t('task:Name.Label'),
      deadline: t('task:Deadline.ErrorMessage'),
    };

    const result = props.task.validate(_localizedFields);
    setErr(result.error);
  }, [t, props.task]);

  useEffect(() => {
    if (appContext.isMobileView) {
      setSelectedPivot('TaskDetails');
    } else if (props.task.taskTypeId) {
      setSelectedPivot('TaskType');
    } else if (props.task.isSeries()) {
      setSelectedPivot('Workflows');
    } else {
      setSelectedPivot('Context');
    }
  }, [appContext.isMobileView, props.task]);

  useEffect(() => {
    //Single task requests to show a specific pivot
    if (props.selectedPivot) {
      setSelectedPivot(props.selectedPivot);
    }
  }, [props.selectedPivot]);

  const validate = (fieldName: string): string | undefined => {
    if (err) {
      const fieldError = err.details.find((e) => {
        return e.context?.key === fieldName || e.message.toLowerCase().includes(fieldName.toLowerCase());
      });

      return fieldError ? fieldError.message : undefined;
    }

    return undefined;
  };

  const hasErrors = (): boolean => {
    //cannot create a template when there are no approvers and when there is no workflow selected
    if (
      props.task.taskType === TaskTypes.EventTemplate &&
      props.task.instances?.length === 0 &&
      workflows.length === 0
    ) {
      return true;
    }

    return err ? true : false;
  };

  const cannotSaveTask = (): boolean => {
    return (props.task.taskId !== -1 && props.task.isEqual(props.orgTask)) || hasErrors();
  };

  const onCancel = () => {
    if (cannotSaveTask()) {
      props.onCancel();
    } else {
      setShowCancelDialog(true);
    }
  };

  const onClickForm = (taskTypeId: number) => {
    const newTask = props.task.clone();
    newTask.assignForm(taskTypeId > 0 ? appContext.globalDataCache.taskTypes.get(taskTypeId) : undefined);

    props.onUpdateTask(newTask);
    if (taskTypeId > 0) {
      setSelectedPivot('TaskType');
    } else if (props.task.isSeries()) {
      setSelectedPivot('Workflows');
    } else {
      setSelectedPivot('Context');
    }
  };

  //
  // Render helpers
  //
  const footer = (
    <IPStack horizontal={!appContext.isMobileView} verticalAlign="end" tokens={globalStackTokensGapSmall}>
      <IPStack.Item>
        <IPStack horizontal tokens={globalStackTokensGapSmall}>
          <IPPrimaryButton
            disabled={cannotSaveTask() || props.isActionPending}
            onClick={async () => {
              props.onSave(props.task, false);
            }}
          >
            {t('translation:General.Button.Save')}
          </IPPrimaryButton>
          <IPDefaultButton
            onClick={() => {
              onCancel();
            }}
          >
            {t('translation:General.Button.Cancel')}
          </IPDefaultButton>
        </IPStack>
      </IPStack.Item>
    </IPStack>
  );

  const getTaskDetails = () => {
    return (
      <ApprovalTemplateTaskDetail
        task={props.task}
        orgTask={props.orgTask}
        onUpdate={(newTask: Task) => {
          props.onUpdateTask(newTask);
        }}
        getErrorMessage={validate}
        isActionPending={props.isActionPending}
        tags={props.tags}
        addTagToTaskState={props.addTagToTaskState}
        removeTagFromTaskState={props.removeTagFromTaskState}
        onChangeCompletionDate={props.onChangeCompletionDate}
        windowLevel={props.windowLevel}
        canUpdate={props.canUpdate}
      />
    );
  };

  //
  // Main render
  //
  return (
    <IPModal forceFocusInsideTrap={false} isOpen={props.isOpen} styles={getTaskViewModalStyles(props.windowLevel)}>
      {props.isLoading && <OverlayLoader text={t('task:Progress.Save')} />}
      <IPStack
        verticalFill
        tokens={globalStackTokensGapSmall}
        styles={getTaskViewModalStackStyles(appContext.isMobileView, false)}
      >
        <IPStack.Item>
          <IPStack horizontal verticalAlign="center" horizontalAlign="space-between">
            <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
              {!appContext.isMobileView && (
                <IPStack.Item>
                  <FontIcon
                    {...templateIcon}
                    style={{
                      fontSize: 28,
                      color: appContext.useDarkMode ? theme.palette.themeLight : theme.palette.themeSecondary,
                    }}
                  />
                </IPStack.Item>
              )}
              <IPStack.Item>
                <IPText variant="xxLarge">
                  {props.task.taskId === -1 && props.task.isSeries() && t('task:Title.Approval.Series.New')}
                  {props.task.taskId !== -1 && props.task.isSeries() && t('task:Title.Approval.Series.Edit')}
                </IPText>
              </IPStack.Item>
            </IPStack>
            {!appContext.isMobileView && (
              <IPStack.Item>
                <TaskTopTasksBar
                  task={props.task}
                  onClickForm={onClickForm}
                  systemTaskType={SystemTaskTypes.Approval}
                  onClickRemove={() => setShowRemoveDialog(true)}
                  onCreateNewTaskTemplate={props.onCreateNewTaskTemplate}
                  onSaveAsNewTask={props.onSaveAsNewTask}
                  navigateExternal={props.navigateExternal}
                  canUpdate={props.canUpdate}
                  canDelete={props.canDelete}
                  orgTask={props.orgTask}
                  canSave={!hasErrors()}
                  isLoading={props.isLoading}
                  isActionPending={props.isActionPending}
                />
              </IPStack.Item>
            )}
            <IPStack.Item>
              <IPIconButton iconProps={cancelIcon} onClick={onCancel} />
            </IPStack.Item>
          </IPStack>
          <IPStack.Item>
            <TaskDetailOwner
              orgTask={props.orgTask}
              task={props.task}
              onUpdate={props.onUpdateTask}
              canUpdate={props.canUpdate}
            />
          </IPStack.Item>
          <ErrorMessage error={props.error} customAction={props.onAfterErrorAction} />
          <IPSeparator />
        </IPStack.Item>
        {appContext.isMobileView && (
          <IPStack.Item>
            <TaskTopTasksBar
              task={props.task}
              onClickForm={onClickForm}
              systemTaskType={SystemTaskTypes.Approval}
              onClickRemove={() => setShowRemoveDialog(true)}
              onCreateNewTaskTemplate={props.onCreateNewTaskTemplate}
              onSaveAsNewTask={props.onSaveAsNewTask}
              navigateExternal={props.navigateExternal}
              canUpdate={props.canUpdate}
              canDelete={props.canDelete}
              orgTask={props.orgTask}
              canSave={!hasErrors()}
              isLoading={props.isLoading}
              isActionPending={props.isActionPending}
            />
          </IPStack.Item>
        )}
        <IPStack.Item grow>
          <IPStack horizontal styles={globalStackStylesHeight100} tokens={globalStackTokensGapSmall}>
            {!appContext.isMobileView && (
              <Fragment>
                <IPStack.Item styles={{ root: { width: '50%' } }}>{getTaskDetails()}</IPStack.Item>
                <IPSeparator vertical></IPSeparator>
              </Fragment>
            )}
            <IPStack.Item styles={!appContext.isMobileView ? { root: { width: '50%' } } : { root: { width: '100%' } }}>
              <IPStack verticalFill tokens={globalStackTokensGapSmall}>
                <IPStack.Item>
                  <IPPivot
                    selectedKey={selectedPivot}
                    onLinkClick={(item) => setSelectedPivot(item?.props.itemKey ?? '')}
                    overflowBehavior="menu"
                  >
                    {appContext.isMobileView && (
                      <IPPivotItem
                        className="isoplanner-task-pivot-details"
                        key="TaskDetails"
                        itemKey="TaskDetails"
                        headerText={t('task:Pivot.TaskDetails')}
                      />
                    )}
                    {props.hasFormFeature && props.task.taskTypeId && props.task.taskTypeId > 0 && (
                      <IPPivotItem
                        className="isoplanner-task-pivot-tasktype"
                        key="TaskType"
                        itemKey="TaskType"
                        headerText={TaskType.tabNameForTask(props.task.taskTypeId, props.task.formVersion, appContext)}
                      />
                    )}
                    {props.task.isSeries() && props.hasPAFeature && (
                      <IPPivotItem
                        className="isoplanner-task-pivot-workflows"
                        key="Workflows"
                        itemKey="Workflows"
                        headerText={t('task:Pivot.Workflows')}
                      />
                    )}
                    {props.task.commentTrailId > 0 && (
                      <IPPivotItem
                        className="isoplanner-task-pivot-activity"
                        key="Activities"
                        itemKey="Activities"
                        headerText={t('task:Pivot.Activities')}
                      />
                    )}
                    {props.task.auditTrailId > 0 && (
                      <IPPivotItem
                        className="isoplanner-task-pivot-changelog"
                        key="Audit"
                        itemKey="Audit"
                        headerText={t('task:Pivot.Audit')}
                      />
                    )}
                  </IPPivot>
                </IPStack.Item>
                {selectedPivot === 'TaskDetails' && (
                  <Fragment>
                    <IPStack.Item grow style={{ height: appContext.isMobileView ? '40vh' : 0 }}>
                      {getTaskDetails()}
                    </IPStack.Item>
                  </Fragment>
                )}
                <TaskTypeForm
                  isOpen={selectedPivot === 'TaskType'}
                  task={props.task}
                  links={props.links}
                  compact={true}
                  readonly={true}
                  onUpdateData={props.onUpdateKPIData}
                  onSetData={props.onSetKPIData}
                  onUpdateTaskForForm={props.onUpdateTaskForForm}
                  addLinks={props.addLinkToTaskState}
                  setLinks={props.setLinkToTaskState}
                  removeLink={props.removeLinkFromTaskState}
                  showErrors={props.showFormErrors}
                  onValidate={props.onValidateForm}
                ></TaskTypeForm>
                {selectedPivot === 'Workflows' && <TaskWorkflows task={props.task} onUpdateWorkflows={setWorkflows} />}
                {selectedPivot === 'Resources' && (
                  <TaskLinkList
                    taskLinks={props.links}
                    task={props.task}
                    addLinks={props.addLinkToTaskState}
                    setLinks={props.setLinkToTaskState}
                    removeLink={props.removeLinkFromTaskState}
                    navigateExternal={props.navigateExternal}
                  />
                )}
                {selectedPivot === 'Activities' && (
                  <IPStack.Item grow>
                    <ActivityTrail
                      auditTrailId={props.task.auditTrailId}
                      commentTrailId={props.task.commentTrailId}
                      taskId={props.task.taskId}
                      authSchemaIds={[props.task.authSchemaId]}
                      onError={props.onError}
                    />
                  </IPStack.Item>
                )}
                {selectedPivot === 'Audit' && (
                  <IPStack.Item grow>
                    <AuditTrail auditTrailId={props.task.auditTrailId} />
                  </IPStack.Item>
                )}
              </IPStack>
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
        <IPStack.Item>
          <IPSeparator styles={{ root: { height: 1 } }} />
        </IPStack.Item>
        <IPStack.Item>{footer}</IPStack.Item>
      </IPStack>
      <DialogConfirmDelete
        onNo={() => {
          setShowRemoveDialog(false);
        }}
        onYes={() => {
          setShowRemoveDialog(false);
          props.onRemove();
        }}
        title={props.task.isSeries() ? t('task:Template.RemoveDialogTitle') : t('task:Remove.DialogTitle')}
        subText={props.task.isSeries() ? t('task:Template.RemoveDialogSubText') : t('task:Remove.DialogSubTitle')}
        hidden={!showRemoveDialog}
        confirmText={!props.task.isSeries() ? t('task:Remove.RemoveDialogConfirmText') : undefined}
      />
      <DialogYesNo
        onNo={() => {
          setShowCancelDialog(false);
        }}
        onYes={() => {
          setShowCancelDialog(false);

          props.onCancel();
        }}
        title={t('task:Cancel.DialogTitle')}
        subText={t('task:Cancel.DialogSubTitle')}
        hidden={!showCancelDialog}
      />
    </IPModal>
  );
};

export default ApprovalTemplateTask;
